import React, { useState, useContext, forwardRef, useImperativeHandle, useEffect } from "react";
import { RadioButton } from "primereact/radiobutton";
import { Slider } from "primereact/slider";
import { InputNumber } from "primereact/inputnumber";
import { AddPropertyContext } from "./AddPropertyContext";
import { redirectToRPData } from "../../../utils/commonUtil";

interface PurchaseDetailsProps {
  onNext: () => void;
  onBack: () => void;
}

interface PurchaseDetailsHandles {
  validate: () => boolean;
}

const PurchaseDetails = forwardRef<PurchaseDetailsHandles, PurchaseDetailsProps>(({ onNext, onBack }, ref) => {
  const context = useContext(AddPropertyContext);
  const [entryType, setEntryType] = useState("percentage");
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  useImperativeHandle(ref, () => ({
    validate: () => {
      const newErrors: { [key: string]: string } = {};
      if (!property.PurchasePrice || property.PurchasePrice === 0) {
        newErrors["PurchasePrice"] = "Purchase amount must be greater than 0.";
      }
      if (!property.EstimatedMarketValue || property.EstimatedMarketValue === 0) {
        newErrors["EstimatedMarketValue"] = "Market value must be greater than 0.";
      }
      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    },
  }));

  if (!context) {
    return <div>Loading...</div>;
  }

  const { property, setProperty } = context;

  useEffect(() => {
    if (context) {
      handleInputChange("Property_PurchaseType", entryType);
    }
  }, [entryType]);

  const handleInputChange = (field: keyof typeof property, value: any) => {
    setProperty({ ...property, [field]: value });
  };

  return (
    <div className="relative rounded-lg p-4 border-solid border border-gray-300 bg-white">
      <div className="default-profile-header-container">
        <h3 className="text-[var(--primary-color)] mt-0 m-1 text-lg">Purchase details</h3>
      </div>
      <div className="flex gap-4 mb-4">
        <div className="flex align-items-center">
          <RadioButton
            inputId="enterAsValue"
            data-testid="appd-rdo-entervalue"
            name="entryType"
            value="value"
            onChange={(e) => setEntryType(e.value as string)}
            checked={entryType === "value"}
          />
          <label htmlFor="enterAsValue" className="ml-2">
            Enter as $ value
          </label>
        </div>
        <div className="flex align-items-center">
          <RadioButton
            inputId="enterAsPercentage"
            data-testid="appd-rdo-enterpercentage"
            name="entryType"
            value="percentage"
            onChange={(e) => setEntryType(e.value as string)}
            checked={entryType === "percentage"}
          />
          <label htmlFor="enterAsPercentage" className="ml-2">
            Enter as % of property purchase price
          </label>
        </div>
      </div>

      <div className="flex flex-row gap-4 mb-4">
        <div className="flex-0 flex-col w-1/2 gap-2">
          <div className="flex items-center">
            <label className="w-1/4">Deposit ({entryType === "value" ? "$" : "%"})</label>

            {entryType === "value" && (
              <Slider
                id="appd-sldr-depositvalue"
                data-testid="appd-sldr-depositvalue"
                value={(property.DepositCash || 0) >= 1500000 ? 1500000 : property.DepositCash || 0}
                onChange={(e: any) => handleInputChange("DepositCash", e.value)}
                className="w-2/4 px-4"
                min={0}
                step={1000}
                max={1500000}
              />
            )}
            {entryType !== "value" && (
              <Slider
                id="appd-sldr-depositpercentage"
                data-testid="appd-sldr-depositpercentage"
                value={property.DepositCash || 0}
                onChange={(e: any) => setTimeout(() => handleInputChange("DepositCash", e.value), 100)}
                className="w-2/4 px-4"
                min={0}
                step={1}
                max={100}
              />
            )}
            <InputNumber
              id="appd-num-deposit"
              data-testid="appd-num-deposit"
              value={property.DepositCash}
              onValueChange={(e) => handleInputChange("DepositCash", e.value)}
              className="w-1/4 ml-3"
              max={entryType === "value" ? undefined : 100}
            />
          </div>
        </div>

        <div className="flex-0 flex-col w-1/2 gap-2">
          <div className="flex items-center">
            <label className="w-1/4">Market value now ($)</label>
            <Slider
              id="appd-sldr-marketvalue"
              data-testid="appd-sldr-marketvalue"
              value={(property.EstimatedMarketValue || 0) >= 1500000 ? 1500000 : property.EstimatedMarketValue || 0}
              onChange={(e: any) => handleInputChange("EstimatedMarketValue", e.value)}
              className="w-2/4 px-4"
              min={0}
              max={1500000}
            />
            <InputNumber
              id="appd-num-marketvalue"
              data-testid="appd-num-marketvalue"
              value={property.EstimatedMarketValue}
              onValueChange={(e) => handleInputChange("EstimatedMarketValue", e.value)}
              className={`w-1/4 ml-3 ${errors.EstimatedMarketValue ? "p-invalid" : ""}`}
            />
          </div>
          {errors.EstimatedMarketValue && (
            <div className="text-red-500 text-sm mt-1 text-right">{errors.EstimatedMarketValue}</div>
          )}
        </div>
      </div>

      <div className="flex flex-row gap-4">
        <div className="flex-1 flex-col w-1/2 gap-2">
          <div className="flex items-center">
            <label className="w-1/4">Purchase amount ($)</label>
            <Slider
              id="appd-sldr-purchaseamount"
              data-testid="appd-sldr-purchaseamount"
              value={(property.PurchasePrice || 0) >= 1500000 ? 1500000 : property.PurchasePrice || 0}
              onChange={(e: any) => handleInputChange("PurchasePrice", e.value)}
              className="w-2/4 px-4"
              min={0}
              max={1500000}
            />
            <InputNumber
              id="appd-num-purchaseamount"
              data-testid="appd-num-purchaseamount"
              value={property.PurchasePrice}
              onValueChange={(e) => handleInputChange("PurchasePrice", e.value)}
              className={`w-1/4 ml-3 ${errors.PurchasePrice ? "p-invalid" : ""}`}
            />
          </div>
          {errors.PurchasePrice && <div className="text-red-500 text-sm mt-1 text-right">{errors.PurchasePrice}</div>}
        </div>
        <div className="flex-1 flex-col w-1/2 gap-2"></div>
      </div>

      <p className="text-sm mt-4">
        * We will provide you with a market estimate at the start of each month that you may choose to use.
        <br />
        For the initial addition of this property however, you can use My valuer to{" "}
        <a
          href="#"
          id="appd-lnk-getestimate"
          data-testid="appd-lnk-getestimate"
          className="text-primary no-underline"
          onClick={redirectToRPData}
        >
          get an estimate
        </a>{" "}
        to add.
      </p>
    </div>
  );
});

export default PurchaseDetails;
